import type { LucideIcon } from 'lucide-react'

export interface PickerIndicatorItem {
  id: string
  name: string
  description?: string
  shortCode?: string
  tier?: 1 | 2
  placement?: 'overlay' | 'pane'
  /** Shown but not addable: the current plan doesn't include it. */
  locked?: boolean
}

export interface PickerIndicatorGroup {
  id: string
  label: string
  description: string
  icon: LucideIcon
  indicatorIds: string[]
}

/** One study on the chart. Several can share an `indicatorId` (EMA 20 + EMA 50). */
export interface ActiveIndicatorInstance {
  studyId: string
  indicatorId: string
  /** Input values summary, e.g. "20 · close" — empty when the study has no inputs. */
  params: string
  visible: boolean
  /** First visible plot color, so the row can be matched to its line on the chart. */
  color: string | null
  /** False for an always-on layer (Percept's volume): the row offers hide/show, no remove. */
  removable?: boolean
  /** False when the panel has no settings view for it. */
  configurable?: boolean
}

export type IndicatorInputValue = string | number | boolean

/** One editable study input, normalized from TV's runtime input metainfo. */
export interface IndicatorSettingsInput {
  id: string
  label: string
  type: 'bool' | 'integer' | 'float' | 'text'
  value: IndicatorInputValue
  /** Present for dropdown inputs (TV 'text' + options, and 'source'). */
  options?: Array<{ value: string; label: string }>
  min?: number
  max?: number
}

/** One plot line of a study, addressed by its style title (TV's override key). */
export interface IndicatorSettingsPlot {
  key: string
  label: string
  color: string
  visible: boolean
}

export interface IndicatorInstanceSettings {
  inputs: IndicatorSettingsInput[]
  plots: IndicatorSettingsPlot[]
}

/** Reads and edits a study in place, so settings apply to the chart live. */
export interface IndicatorSettingsApi {
  read: (studyId: string) => IndicatorInstanceSettings | null
  setInput: (studyId: string, inputId: string, value: IndicatorInputValue) => void
  setPlot: (studyId: string, plotKey: string, patch: { color?: string; visible?: boolean }) => void
}

/** Normalizes any CSS color TV hands back to #rrggbb for a native color input. */
export function toHexColor(color: string): string | null {
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})([0-9a-f]{2})?$/i)?.[1]
  if (hex) {
    return `#${hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex}`.toLowerCase()
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (!rgb) return null
  return `#${rgb
    .slice(1, 4)
    .map((part) => Math.min(255, Number(part)).toString(16).padStart(2, '0'))
    .join('')}`
}

/** Profiles are on/off chart settings, not stackable studies: at most one on the chart. */
export const SINGLE_INSTANCE_INDICATOR_IDS = new Set([
  'volumeDeltaProfile',
  'liquidationProfile',
  'openInterestProfile',
  'liquidationLevelProfile',
  'stopLevelProfile',
  'orderbookHeatmap',
])

export function getIndicatorShortCode(name: string, id: string) {
  const cleanId = id.replace(/^orderx-/, '')
  const words = name.match(/[A-Z]+(?=[A-Z][a-z]|\b)|[A-Z]?[a-z]+|\d+/g)
  const code = words
    ?.map((word) => word[0])
    .join('')
    .slice(0, 5)
  return (code || cleanId.slice(0, 5)).toUpperCase()
}

export function formatIndicatorName(name: string) {
  return name.replace(/^OrderX\s+/, '')
}
